// DELIVERY PRICING ENGINE
//
// Drop-in replacement for the old src/data/pincodes.js. Every export the old
// file had is still here with the same name and the same shape, so
// PincodeCheck.jsx, GetQuote.jsx, Policies.jsx, lib/quote.js and lib/api.js
// keep working without a change.
//
// What it does differently: it prices from what the trip actually costs you,
// rather than from a band table someone guessed at once.
//
//   1. The pincode maps to a real coordinate (see ncrPincodes.js - India Post
//      post-office positions, cleaned).
//   2. Straight-line distance to BOTH dispatch points; the nearer one wins.
//   3. That is converted to road distance with a multiplier, per corridor,
//      because the drive to Noida crosses the Yamuna and the drive to Gurugram
//      does not.
//   4. The order picks a vehicle by how many units it is.
//   5. Your cost for that vehicle over that distance is computed.
//   6. GST is taken out of what the customer pays, because the customer's money
//      is not all yours.
//   7. What is left has to beat the cost, with margin, or the price goes up.
//
// The point of steps 5 to 7 is that a price can never be set without knowing
// the cost it has to cover. Under the old band table it could, and did.
//
// ---------------------------------------------------------------------------
// WHAT IS MEASURED AND WHAT IS NOT            (calibrated 27 Aug 2026)
//
//   MEASURED, from 32 real Porter quotes:
//     - the road multiplier (1.38) and five corridor multipliers
//     - the 3-wheeler cost curve, and the extra it costs to leave Delhi
//     - 32 exact pincode distances, in VERIFIED_KM
//
//   STILL ESTIMATED:
//     - the Tata Ace, pickup and 407 curves. Scaled from the 3-wheeler. Every
//       order above 2 units rests on this.
//     - `handling`, at 80 rupees a trip. On a local delivery that is 14% of the
//       cost - the largest unverified number left in this file.
//     - 23 pincodes in Gurugram and Ghaziabad still sitting on coordinates that
//       a neighbour's measurement disproved. 122001 and 201001 are among them.
//       See calibration-round2.csv.
// ---------------------------------------------------------------------------

import { NCR_PINCODES } from './ncrPincodes';

// ===========================================================================
// 1. DISPATCH POINTS
// ===========================================================================
// Both verified against India Post's own coordinates for their pincodes, which
// agree with the figures that were already in this file to within 100 m.

export const ORIGINS = [
  {
    id: '110074',
    label: 'Chhattarpur',
    // Plot No. 50, Kh No. 329, Chhattarpur Village, South West Delhi 110074
    lat: 28.5073,
    lng: 77.1846,
    primary: true,
  },
  {
    id: '110049',
    label: 'South Extension',
    // 13 Jain Mandir Market, South Extension Part 2, New Delhi 110049
    lat: 28.5579,
    lng: 77.2132,
  },
];

// ===========================================================================
// 2. WHAT A TRIP COSTS YOU          <-- THE NUMBERS THAT MATTER MOST
// ===========================================================================
// One entry per vehicle you would actually book, smallest first. The engine
// picks the first one that fits the order.
//
// baseFare    what the booking costs before distance, including includedKm
// includedKm  road km already inside baseFare
// perKm       charged for each km beyond includedKm
// handling    loading, unloading, waiting, the lift, the guard at the gate.
//             Round up. This is where a delivery quietly eats its margin.
// maxUnits    how many indoor+outdoor sets fit. Volume decides this, not
//             weight - a split AC set is about 0.4 m3 and only 50 kg.
//
// VERIFY EVERY ROW. Book a trip, read the invoice, type the number in.

export const LOADER = {
  // MEASURED, on 32 real Porter quotes taken 27 Aug 2026.
  //
  // What was quoted: the cheaper of an e-3-wheeler and a 3-wheeler where both
  // were offered, and the 3-wheeler alone where the electric one was not
  // available. That is exactly what you would actually book, so it is exactly
  // what the price should be built on.
  //
  // Fitted to minimise RELATIVE error, not absolute. Least squares put the
  // intercept at 434, which is fine on a 2000-rupee trip and ruinous on a
  // 300-rupee one — it read the 0.5 km delivery as costing 456 when it cost
  // 304, and would have charged 850 for it. Margin is a percentage, so the cost
  // model has to be accurate in percentage terms too.
  id: '3w',
  label: '3-wheeler',
  maxUnits: 2,
  baseFare: 300,
  includedKm: 0,
  perKm: 24.0,
  handling: 80,

  // Trips that leave Delhi cost more than the same distance inside it. The
  // quotes show it plainly: 11-15 km inside Delhi ran a median 600, the same
  // distance into Haryana or UP ran 781. A driver dropping in Noida or Gurugram
  // has a harder time finding a return load, and prices for it.
  //
  // This term is what takes the sample from five under-priced trips to two. It
  // does NOT improve average error - it slightly worsens it - but average error
  // is the wrong target. The job is not being under cost.
  borderCharge: 125,
};

// ---------------------------------------------------------------------------
// BIGGER VEHICLES — deliberately empty.
//
// A Tata Ace or a pickup would carry a 4-unit order in one trip and cost less
// than two 3-wheeler trips. But no Ace fare has ever been quoted, and an
// invented one would sit in the pricing path pretending to be knowledge.
//
// So while this list is empty, a large order is priced as however many
// 3-wheeler trips it actually takes. That is measured, and it can only ever
// over-charge you into profit, never under-charge you into a loss. If you send
// one Ace instead, you keep the difference.
//
// The moment you happen to book an Ace for anything, read the fare off the
// invoice and add it here — one entry is enough, the engine picks it up:
//
//   { id: 'ace', label: 'Tata Ace', maxUnits: 5,
//     baseFare: ___, includedKm: 0, perKm: ___, handling: 150, borderCharge: ___ },
//
// Two fares at different distances let tools/calibrate.mjs fit perKm properly.
// One fare is still better than nothing: use it as baseFare with perKm copied
// up from the 3-wheeler.
// ---------------------------------------------------------------------------
export const LARGER_VEHICLES = [];

export const VEHICLES = [LOADER, ...LARGER_VEHICLES];

// Things that happen to a real delivery and not to a spreadsheet.
export const RISK = {
  // Porter and everyone else surge in peak hours, in the rain, and at month
  // end. Averaged over a year this is the uplift on the quoted fare.
  surge: 0.10,

  // Share of deliveries that have to be attempted twice - nobody home, lift
  // out, society will not allow it before 10am. Each one costs a second trip.
  reattemptRate: 0.04,

  // Whether you can claim input credit on the freight invoice. Porter bills
  // freight; whether that credit is available to you depends on how they
  // invoice and how you are registered. Ask your CA - it moves your true cost
  // by the full GST amount. Set to true only once you have been told yes.
  freightGstCreditable: false,
  freightGstRate: 0.05,
};

// ===========================================================================
// 3. STRAIGHT LINE TO ROAD
// ===========================================================================
// Crow-flight distance always understates the drive. How badly depends on the
// direction: the Yamuna and the Aravallis both force detours, and Greater Noida
// is further by road than it looks on a map.
//
// Fitted from measured trips by tools/calibrate.mjs. Re-run it whenever you add
// rows to a calibration sheet.

export const ROAD = {
  // MEASURED. Theil-Sen fit over 25 clean trips, Aug 2026. Theil-Sen rather than
  // least squares because two long rural trips sit at the far end of the sample
  // and carry huge leverage — an OLS fit on the same data returned 1.79 with a
  // negative intercept, which is an artefact, not a road network.
  defaultMultiplier: 1.38,

  // A city only gets its own multiplier where two or more measured trips of 8 km
  // or more support one, AND the result comes out above the global rate. Below
  // the global it is far more likely to be noise than a genuinely straighter
  // road, and believing it would under-price every future trip there.
  //
  // Everything not listed uses 1.38 deliberately. With one trip per corridor
  // there is no corridor resolution to claim.
  multipliers: {
    Bahadurgarh: 1.87,        // 2 trips, both 33 km+. Rural Jhajjar roads really
                              // do run this indirect — measured, not guessed.
    'West Delhi': 1.46,       // 2 trips
    'North Delhi': 1.44,      // 2 trips
    'North East Delhi': 1.43, // 2 trips, across the Yamuna
    Faridabad: 1.39,          // 2 trips
  },

  // Gate to gate, the wrong side of the sector, the U-turn. The measured data
  // put this near zero — at NCR distances it is already inside the multiplier —
  // so this is a floor to stop a 1 km trip being priced as a 0 km one.
  accessKm: 0.5,

  // How much of a low-confidence pincode's coordinate error to assume against
  // yourself. 1.0 would price every uncertain pincode at its worst case, which
  // overcharges honest customers; 0 would ignore the risk entirely.
  uncertaintyWeight: 0.6,
};

// Real measured road distances, one way, from the named origin. These beat
// everything above - no multiplier, no uncertainty margin, no estimate.
//
// Measured in Porter, Aug 2026. Six of these contradicted their own coordinate
// (two reported a road distance SHORTER than the straight line, which is
// impossible) - those coordinates are wrong and these figures replace them
// outright. See CALIBRATION-NOTES.md for which neighbours are still affected.
//
// Fill more from calibration.csv. Every row added here is a pincode the engine
// stops guessing about.
export const VERIFIED_KM = {
  '110029': { km: 4, from: 'South Extension' },
  '110076': { km: 11.5, from: 'South Extension' },
  '110049': { km: 0.5, from: 'South Extension' },
  '110044': { km: 19, from: 'South Extension' },
  '110066': { km: 5, from: 'South Extension' },
  '110102': { km: 27, from: 'South Extension' },
  '110072': { km: 38, from: 'South Extension' },
  '110078': { km: 25, from: 'South Extension' },
  '110012': { km: 16.5, from: 'South Extension' },
  '110043': { km: 31, from: 'Chhattarpur' },
  '122002': { km: 12, from: 'Chhattarpur' },
  '122009': { km: 14.5, from: 'Chhattarpur' },
  '201008': { km: 51, from: 'South Extension' },
  '201318': { km: 28, from: 'South Extension' },
  '121001': { km: 25, from: 'Chhattarpur' },
  '121007': { km: 27, from: 'Chhattarpur' },
  '201020': { km: 17.6, from: 'South Extension' },
  '201019': { km: 19.5, from: 'South Extension' },
  '110091': { km: 13.5, from: 'South Extension' },
  '110096': { km: 13.3, from: 'South Extension' },
  '110106': { km: 17, from: 'South Extension' },
  '110033': { km: 29, from: 'South Extension' },
  '110056': { km: 25, from: 'South Extension' },
  '110110': { km: 27, from: 'South Extension' },
  '110060': { km: 12.3, from: 'South Extension' },
  '110005': { km: 15, from: 'South Extension' },
  '110151': { km: 17, from: 'South Extension' },
  '110195': { km: 21.5, from: 'South Extension' },
  '110053': { km: 21, from: 'South Extension' },
  '110090': { km: 28, from: 'South Extension' },
  '124506': { km: 60, from: 'South Extension' },
  '124107': { km: 75, from: 'Chhattarpur' },
};

// ===========================================================================
// 4. WHAT THE CUSTOMER PAYS
// ===========================================================================

export const PRICING = {
  // On top of cost. Delivery is not where an AC business makes its money, but
  // it must not be where it loses any either.
  marginPct: 0.15,

  // No delivery is cheaper than this however close it is - a trip has a floor.
  minCharge: 350,

  // Displayed prices land on a multiple of this. Always rounded UP, so
  // rounding can only ever help you.
  roundTo: 50,

  // The figure shown to the customer already contains GST. This matches what
  // PincodeCheck.jsx tells them ("GST included").
  //
  // IMPORTANT: delivery billed along with the goods is a composite supply and
  // is taxed at the rate of the goods. For air conditioners (HSN 8415) that is
  // 18%, cut from 28% by the 56th GST Council with effect from 22 Sep 2025.
  // This matches GST_RATE in data/pricing.js — keep the two in step.
  //
  // So of a GST-inclusive 800 rupee delivery charge you keep 678. If this file
  // thought it was keeping 800 it would be pricing a trip against money that
  // belongs to the government. It does not.
  priceIncludesGst: true,
  gstRate: 0.18,
};

// Orders at or above this get free delivery. Note that free delivery is not
// free - deliveryCharge() reports what you absorb in `absorbedCost`, so you can
// see the real cost of this policy in your lead data rather than guess at it.
export const FREE_ABOVE = 100000;

// Beyond this road distance, not serviceable at any price.
export const MAX_ROAD_KM = 65;

// Kept because the old file exported it. It is now derived: straight-line
// equivalent of MAX_ROAD_KM at the default multiplier.
export const MAX_KM = Math.round(MAX_ROAD_KM / ROAD.defaultMultiplier);

// A fixed all-inclusive price for a pincode. Beats every calculation above.
// Use for a society you know costs more, or a rate you have already agreed.
export const OVERRIDES = {
  // '110049': 0,
};

// Pincodes you will not deliver to whatever the maths says.
export const BLOCKED = [];

export const OUT_OF_AREA_MESSAGE =
  'We deliver within Delhi NCR only. Call us on 7703900307 if you are just outside — we will tell you honestly whether we can help.';

// ===========================================================================
// 5. THE MATHS
// ===========================================================================

const UNCERTAINTY_KM = { H: 1.0, M: 2.5, L: 5.0, I: 7.5 };

const clean = (v) => String(v || '').replace(/\D/g, '').slice(0, 6);
export const isValidPincode = (v) => /^[1-9]\d{5}$/.test(clean(v));

const ceilTo = (v, step) => Math.ceil(v / step) * step;

// Haversine. R = 6371.0088 km, the mean Earth radius.
export function distanceKm(a, b) {
  const R = 6371.0088;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Live pincodes India Post's coordinate file leaves out.
//
// A pincode whose post offices carry no coordinates never made it into
// NCR_PINCODES, so the customer was told we do not deliver: 122022 in Gurugram
// was one, and the whole run 122019-122050 is missing. For those, borrow the
// location of the nearest-numbered pincode in the same postal block (same
// first five digits, else the same first four, where the block already has at
// least three listed pincodes). The row is marked 'I' and its distance widened
// more than any listed pincode, so the charge can only err high. Anything
// outside a block we serve - Mumbai, Jaipur - still gets no answer.
const INFERRED = {};
function inferPin(pin) {
  if (Object.prototype.hasOwnProperty.call(INFERRED, pin)) return INFERRED[pin];
  let row = null;
  const n = Number(pin);
  for (const len of [5, 4]) {
    const prefix = pin.slice(0, len);
    const peers = Object.keys(NCR_PINCODES).filter((k) => k.startsWith(prefix));
    if (peers.length >= (len === 5 ? 1 : 3)) {
      const nearest = peers.reduce((a, b) => (Math.abs(Number(b) - n) < Math.abs(Number(a) - n) ? b : a));
      const [lat, lng, city, state] = NCR_PINCODES[nearest];
      row = [lat, lng, city, state, 'I'];
      break;
    }
  }
  INFERRED[pin] = row;
  return row;
}

function readPin(pin) {
  const row = NCR_PINCODES[pin] || inferPin(pin);
  if (!row) return null;
  const [lat, lng, city, state, conf] = row;
  return { lat, lng, city, state, conf };
}

// Nearest dispatch point by straight line. Exported because the old file
// exported it; it takes a { lat, lng } now rather than an area object.
export function nearestOrigin(point) {
  return ORIGINS.map((o) => ({ origin: o, km: distanceKm(o, point) })).sort(
    (a, b) => a.km - b.km,
  )[0];
}

// Straight line -> the distance the van actually drives.
function roadDistance(pin, area) {
  const verified = VERIFIED_KM[pin];
  if (verified && verified.km > 0) {
    const origin =
      ORIGINS.find((o) => o.label === verified.from) || nearestOrigin(area).origin;
    return { roadKm: verified.km, crowKm: distanceKm(origin, area), origin, verified: true };
  }

  const { origin, km: crowKm } = nearestOrigin(area);
  const mult = ROAD.multipliers[area.city] ?? ROAD.defaultMultiplier;
  const uncertainty = (UNCERTAINTY_KM[area.conf] ?? 2.5) * ROAD.uncertaintyWeight;
  const roadKm = (crowKm + uncertainty) * mult + ROAD.accessKm;
  return { roadKm, crowKm, origin, verified: false, multiplier: mult, uncertainty };
}

// Which vehicle the order needs.
export function vehicleFor(units) {
  const u = Math.max(1, Math.ceil(units || 1));
  return VEHICLES.find((v) => u <= v.maxUnits) || VEHICLES[VEHICLES.length - 1];
}

// How many runs the order needs in that vehicle. With only the 3-wheeler on the
// books this is what makes a 4-unit order cost two trips instead of guessing at
// a Tata Ace fare nobody has ever paid.
export function tripsFor(units, vehicle = vehicleFor(units)) {
  const u = Math.max(1, Math.ceil(units || 1));
  return Math.max(1, Math.ceil(u / vehicle.maxUnits));
}

// What the trip costs YOU. Everything downstream is built on this.
export function tripCost(roadKm, vehicle, { crossesBorder = false } = {}) {
  const distance = vehicle.perKm * Math.max(0, roadKm - vehicle.includedKm);
  const border = crossesBorder ? (vehicle.borderCharge || 0) : 0;
  const fare = vehicle.baseFare + distance + border;

  // Freight GST is a real cost unless you can credit it.
  const gstOnFreight = RISK.freightGstCreditable ? 0 : fare * RISK.freightGstRate;

  const beforeRisk = fare + gstOnFreight + vehicle.handling;
  const withSurge = beforeRisk * (1 + RISK.surge);
  const withReattempts = withSurge * (1 + RISK.reattemptRate);

  return {
    total: withReattempts,
    fare,
    distanceComponent: distance,
    border,
    handling: vehicle.handling,
    gstOnFreight,
    surge: withSurge - beforeRisk,
    reattempts: withReattempts - withSurge,
  };
}

// Cost -> the number on the screen.
function priceFromCost(cost) {
  const wanted = cost * (1 + PRICING.marginPct);

  // The customer's payment is not all yours. Gross the target up so that what
  // survives GST is still the number you wanted.
  const gross = PRICING.priceIncludesGst ? wanted * (1 + PRICING.gstRate) : wanted;

  return Math.max(PRICING.minCharge, ceilTo(gross, PRICING.roundTo));
}

// What you actually keep out of a price, after GST.
function netOf(price) {
  return PRICING.priceIncludesGst ? price / (1 + PRICING.gstRate) : price;
}

// ===========================================================================
// 6. THE API THE SITE CALLS
// ===========================================================================

export function lookupPincode(input) {
  const pin = clean(input);

  if (!isValidPincode(pin)) {
    return { ok: false, reason: 'invalid', message: 'Enter a valid 6-digit pincode.' };
  }
  if (BLOCKED.includes(pin)) {
    return { ok: false, reason: 'blocked', pin, message: OUT_OF_AREA_MESSAGE };
  }

  const area = readPin(pin);
  if (!area) {
    return { ok: false, reason: 'outside-ncr', pin, message: OUT_OF_AREA_MESSAGE };
  }

  const { roadKm, crowKm, origin, verified } = roadDistance(pin, area);

  if (roadKm > MAX_ROAD_KM) {
    return {
      ok: false,
      reason: 'too-far',
      pin,
      area: area.city,
      km: Math.round(roadKm * 10) / 10,
      message: OUT_OF_AREA_MESSAGE,
    };
  }

  const fixed = OVERRIDES[pin] != null;
  const crossesBorder = area.state !== 'Delhi';
  const vehicle = vehicleFor(1);
  const cost = tripCost(roadKm, vehicle, { crossesBorder });
  const basePrice = fixed ? OVERRIDES[pin] : priceFromCost(cost.total);

  return {
    ok: true,
    pin,
    zone: area.city,          // what the old file called zone
    city: area.city,
    state: area.state,
    basePrice,
    km: Math.round(roadKm * 10) / 10,
    crowKm: Math.round(crowKm * 10) / 10,
    band: bandLabel(roadKm),
    dispatchFrom: origin.label,
    fixed,

    // new, and worth logging on every lead
    confidence: verified ? 'verified' : { H: 'high', M: 'medium', L: 'low' }[area.conf],
    estimated: !verified,
    crossesBorder,
  };
}

// Per order, not per unit - one trip carries several ACs.
export function deliveryCharge(result, { orderValue = 0, units = 1 } = {}) {
  if (!result?.ok) return null;

  const vehicle = vehicleFor(units);
  const trips = tripsFor(units, vehicle);
  const one = tripCost(result.km, vehicle, { crossesBorder: result.crossesBorder });
  const cost = { ...one, total: one.total * trips, perTrip: one.total };
  const bulk = trips > 1;

  const price = result.fixed
    ? OVERRIDES[result.pin]
    : priceFromCost(cost.total);

  if (FREE_ABOVE != null && orderValue >= FREE_ABOVE) {
    return {
      price: 0,
      free: true,
      reason: 'threshold',
      zone: result.zone,
      km: result.km,
      dispatchFrom: result.dispatchFrom,
      vehicle: vehicle.label,
      trips,
      // What this free delivery actually costs you. Log it.
      absorbedCost: Math.round(cost.total),
      confidence: result.confidence,
    };
  }

  const net = netOf(price);

  return {
    price,
    free: false,
    bulk,
    basePrice: result.basePrice,
    surchargePct: result.basePrice ? Math.round((price / result.basePrice - 1) * 100) : 0,
    zone: result.zone,
    km: result.km,
    dispatchFrom: result.dispatchFrom,

    // new
    vehicle: vehicle.label,
    trips,
    cost: Math.round(cost.total),
    net: Math.round(net),
    marginRs: Math.round(net - cost.total),
    marginPct: Math.round(((net - cost.total) / cost.total) * 100),
    belowCost: net < cost.total,   // must never be true. See tests.
    confidence: result.confidence,
  };
}

// ===========================================================================
// 7. COMPATIBILITY WITH WHAT THE SITE ALREADY IMPORTS
// ===========================================================================
// The old file exported a hand-written band table and the policy page rendered
// it. Bands are now DERIVED from the formula, by pricing a single-unit order at
// the top of each band. Published rates and charged rates therefore cannot
// drift apart, which under the old file they silently could.

const BAND_EDGES = [
  { maxKm: 8, label: 'Local' },
  { maxKm: 15, label: 'Near' },
  { maxKm: 25, label: 'Mid' },
  { maxKm: 40, label: 'Far' },
  { maxKm: MAX_ROAD_KM, label: 'Outer NCR' },
];

function bandLabel(roadKm) {
  return (BAND_EDGES.find((b) => roadKm <= b.maxKm) || BAND_EDGES[BAND_EDGES.length - 1]).label;
}

// A band's published price is the price at the TOP of that band, and for a trip
// that leaves Delhi, because that is the dearer of the two. Publishing the Delhi
// figure would have quoted a Gurugram customer less on the policy page than the
// cart then charged them - the exact drift this table exists to prevent.
export const DISTANCE_BANDS = BAND_EDGES.map((b) => ({
  maxKm: b.maxKm,
  label: b.label,
  price: priceFromCost(tripCost(b.maxKm, LOADER, { crossesBorder: true }).total),
  priceInDelhi: priceFromCost(tripCost(b.maxKm, LOADER, { crossesBorder: false }).total),
  priceOutsideDelhi: priceFromCost(tripCost(b.maxKm, LOADER, { crossesBorder: true }).total),
}));

// For the delivery policy page, so published and charged rates cannot drift.
export const bandTable = () => DISTANCE_BANDS.map((b) => ({ ...b }));

// For the policy page: what an order costs by UNIT COUNT, which is what the
// customer actually has. They never see a vehicle.
export const unitTable = (kmSamples = [10, 25, 40]) =>
  [1, 2, 3, 4, 6].map((units) => {
    const v = vehicleFor(units);
    const trips = tripsFor(units, v);
    const row = { units, vehicle: v.label, trips };
    kmSamples.forEach((km) => {
      row[`at${km}km`] = priceFromCost(tripCost(km, v).total * trips);
    });
    return row;
  });

// Kept because the old file exported it and Policies.jsx may still import it.
export const vehicleTable = () =>
  VEHICLES.map((v) => ({
    label: v.label,
    upToUnits: v.maxUnits,
    priceAt10km: priceFromCost(tripCost(10, v).total),
    priceAt25km: priceFromCost(tripCost(25, v).total),
    priceAt40km: priceFromCost(tripCost(40, v).total),
  }));

// Kept for Policies.jsx. Above the threshold the order needs a second run, so
// it costs a second trip - not a made-up 35%.
export const BULK_THRESHOLD = LOADER.maxUnits;
export const BULK_SURCHARGE =
  priceFromCost(tripCost(20, LOADER).total * 2) /
    priceFromCost(tripCost(20, LOADER).total) -
  1;

// How much more the customer needs to spend to cross the free-delivery line.
export function amountToFreeDelivery(orderValue) {
  if (FREE_ABOVE == null) return null;
  const gap = FREE_ABOVE - orderValue;
  return gap > 0 ? gap : null;
}

// The old file exported AREAS and some code may still read it. Rebuilt from the
// pincode table so nothing that imports it breaks.
export const AREAS = Object.entries(
  Object.keys(NCR_PINCODES).reduce((acc, pin) => {
    const [lat, lng, city, state] = NCR_PINCODES[pin];
    (acc[city] ||= { label: city, city, state, lat, lng, pins: [] }).pins.push(Number(pin));
    return acc;
  }, {}),
).map(([, v]) => v);

export default {
  lookupPincode,
  deliveryCharge,
  isValidPincode,
  bandTable,
  unitTable,
  vehicleTable,
  amountToFreeDelivery,
  nearestOrigin,
  vehicleFor,
  tripsFor,
  tripCost,
};
